import { z } from 'zod';
import { TenantProductSchema, PRODUCT_KEYS, PRODUCT_REGISTRY, type TenantProduct } from '../products';

/**
 * Catalogo globale delle modalità di pagamento del cliente all'insegna
 * (`retail_brand_payment_modes`): TEMPLATE di finanziamento per insegna.
 *
 * Piano COMMERCIALE (come il cliente paga all'insegna il device/canvass: VAR,
 * Easypay, finanziamento ente terzo): deciso dal listino sulla composizione
 * bundle, ereditato in cassa. Distinto dal piano INCASSO (`pos_payment_methods`,
 * scelto in cassa). Una vendita senza modalità = non finanziata.
 *
 * Ogni modalità appartiene a un'insegna (`brand_id` NOT NULL) ed è sempre
 * finanziata: chi finanzia è l'insegna stessa o uno degli enti globali ammessi.
 */

/** Chi detiene il credito (ente che finanzia): insegna stessa | ente terzo. */
export const PAYMENT_MODE_CREDIT_HOLDERS = ['brand', 'third_party'] as const;
export type PaymentModeCreditHolder = (typeof PAYMENT_MODE_CREDIT_HOLDERS)[number];
/** Etichette "Ente che finanzia" (unica fonte per master, tenant e gare). */
export const PAYMENT_MODE_CREDIT_HOLDER_LABELS: Record<PaymentModeCreditHolder, string> = {
  brand: 'Insegna stessa (esclusiva)',
  third_party: 'Ente terzo (uno o più enti ammessi)',
};

/** Etichette brevi (badge, colonne strette). */
export const PAYMENT_MODE_CREDIT_HOLDER_SHORT_LABELS: Record<PaymentModeCreditHolder, string> = {
  brand: 'Insegna',
  third_party: 'Ente terzo',
};

/**
 * Addebito: come il cliente rimborsa le rate all'ente (RID, Carta, credito
 * residuo SIM). Il template dichiara gli addebiti AMMESSI (`allowed_debits`),
 * la composizione bundle sceglie quello EFFETTIVO. NON è come il cliente paga
 * a noi né come paga il canone.
 */
export const INSTALLMENT_REPAYMENTS = ['rid', 'cdc', 'sim_credit'] as const;
export type InstallmentRepayment = (typeof INSTALLMENT_REPAYMENTS)[number];

/** Etichette estese del rimborso rate (unica fonte BE+FE). */
export const INSTALLMENT_REPAYMENT_LABELS: Record<InstallmentRepayment, string> = {
  rid: 'RID',
  cdc: 'Carta di credito',
  sim_credit: 'Credito residuo SIM',
};

/** Etichette abbreviate (badge, chip, colonne strette). */
export const INSTALLMENT_REPAYMENT_SHORT_LABELS: Record<InstallmentRepayment, string> = {
  rid: 'RID',
  cdc: 'CdC',
  sim_credit: 'SIM',
};

/** Label sicura per valori nullable (NULL = non applicabile). */
export function installmentRepaymentLabel(
  value: InstallmentRepayment | null | undefined,
  variant: 'long' | 'short' = 'long',
): string | null {
  if (!value) return null;
  return variant === 'short' ? INSTALLMENT_REPAYMENT_SHORT_LABELS[value] : INSTALLMENT_REPAYMENT_LABELS[value];
}

/** Addebiti ammessi dal template (≥1). */
export const PaymentModeAllowedDebitsSchema = z.array(z.enum(INSTALLMENT_REPAYMENTS)).min(1, 'Indicare almeno un addebito ammesso');
export const PAYMENT_MODE_ALLOWED_DEBITS_LABEL = 'Addebiti ammessi';

/** Attesa contabile: come arriva il denaro della quota finanziata. */
export const PAYMENT_MODE_ACCOUNTING_EXPECTATIONS = ['entity_bank_transfer', 'brand_statement_credit', 'none'] as const;
export type PaymentModeAccountingExpectation = (typeof PAYMENT_MODE_ACCOUNTING_EXPECTATIONS)[number];
export const PAYMENT_MODE_ACCOUNTING_EXPECTATION_LABELS: Record<PaymentModeAccountingExpectation, string> = {
  entity_bank_transfer: 'Bonifico dall\'ente',
  brand_statement_credit: 'Accredito in estratto conto insegna',
  none: 'Nessuna',
};
export const PAYMENT_MODE_ACCOUNTING_EXPECTATION_LABEL = 'Contabilmente';
export const PAYMENT_MODE_ADMIN_EXPECTATION_LABEL = 'Amministrativamente';

/** Attesa amministrativa generata dalla vendita verso l'ente. */
export const PAYMENT_MODE_ADMIN_EXPECTATIONS = ['credit_assignment', 'credit_note', 'none'] as const;
export type PaymentModeAdminExpectation = (typeof PAYMENT_MODE_ADMIN_EXPECTATIONS)[number];
export const PAYMENT_MODE_ADMIN_EXPECTATION_LABELS: Record<PaymentModeAdminExpectation, string> = {
  credit_assignment: 'Cessione del credito',
  credit_note: 'Nota di credito',
  none: 'Nessuna',
};

/** Codici RT ammessi per la quota finanziata (stesso dominio di pos_payment_methods). */
export const PaymentModeAdeCodeSchema = z.union([
  z.literal(0), z.literal(1), z.literal(2), z.literal(3), z.literal(5),
]);
export type PaymentModeAdeCode = z.infer<typeof PaymentModeAdeCodeSchema>;

const MpCodeSchema = z.string().regex(/^MP\d{2}$/, 'Codice fattura non valido (MPnn)');

/** Riga del catalogo (response). */
// ─── Task #4334 — prodotti abilitati ("Disponibile per") ───

/** Titolo del campo "Disponibile per" (prodotti abilitati). */
export const PAYMENT_MODE_AVAILABLE_PRODUCTS_LABEL = 'Disponibile per';

/** Prodotti per cui la modalità è abbinabile (`available_products`; default = tutti). */
export const PaymentModeAvailableProductsSchema = z.array(TenantProductSchema).min(1, 'Indicare almeno un prodotto');

/** Etichetta prodotto per "Disponibile per" (dal registry prodotti). */
export const TENANT_PRODUCT_LABELS: Record<TenantProduct, string> = Object.fromEntries(
  PRODUCT_REGISTRY.map((p) => [p.key, p.label]),
) as Record<TenantProduct, string>;

export const ALL_TENANT_PRODUCTS: readonly TenantProduct[] = PRODUCT_KEYS;

/** Ente globale ammesso da una modalità (preset `legal_entities` senza tenant). */
export const PaymentModeAllowedEntitySchema = z.object({
  id: z.string().uuid(),
  legal_name: z.string(),
  trade_name: z.string().nullable(),
  vat_number: z.string().nullable(),
  is_active: z.boolean(),
});
export type PaymentModeAllowedEntity = z.infer<typeof PaymentModeAllowedEntitySchema>;

export const RetailBrandPaymentModeSchema = z.object({
  id: z.string().uuid(),
  brand_id: z.string().uuid(),
  brand_name: z.string(),
  code: z.string(),
  name: z.string(),
  credit_holder: z.enum(PAYMENT_MODE_CREDIT_HOLDERS),
  allowed_debits: PaymentModeAllowedDebitsSchema,
  /** Enti globali ammessi (vuoto con titolare insegna). */
  allowed_entities: z.array(PaymentModeAllowedEntitySchema),
  accounting_expectation: z.enum(PAYMENT_MODE_ACCOUNTING_EXPECTATIONS),
  admin_expectation: z.enum(PAYMENT_MODE_ADMIN_EXPECTATIONS),
  ade_code: PaymentModeAdeCodeSchema.nullable(),
  mp_code: z.string().nullable(),
  /** Task #4334 — prodotti abilitati (easyflow | w3suite). */
  available_products: PaymentModeAvailableProductsSchema,
  is_active: z.boolean(),
  sort_order: z.number().int(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type RetailBrandPaymentMode = z.infer<typeof RetailBrandPaymentModeSchema>;

export const RetailBrandPaymentModeListSchema = z.array(RetailBrandPaymentModeSchema);

const optStr = z.string().trim().min(1).max(64).optional();

const PaymentModeBaseSchema = z.object({
  brand_id: z.string().uuid({ message: 'Insegna obbligatoria' }),
  code: optStr,
  name: z.string().trim().min(1, 'Nome commerciale obbligatorio').max(128),
  credit_holder: z.enum(PAYMENT_MODE_CREDIT_HOLDERS),
  allowed_debits: PaymentModeAllowedDebitsSchema,
  /** Id degli enti globali ammessi (obbligatorio ≥1 con ente terzo, vuoto con insegna). */
  allowed_entity_ids: z.array(z.string().uuid()).optional(),
  accounting_expectation: z.enum(PAYMENT_MODE_ACCOUNTING_EXPECTATIONS),
  admin_expectation: z.enum(PAYMENT_MODE_ADMIN_EXPECTATIONS),
  ade_code: PaymentModeAdeCodeSchema.nullable().optional(),
  mp_code: MpCodeSchema.nullable().optional(),
  available_products: PaymentModeAvailableProductsSchema.optional(),
  is_active: z.boolean().optional(),
  sort_order: z.number().int().optional(),
});

/** Invarianti di forma (speculari ai CHECK DB), applicati sul payload completo. */
export function refinePaymentModeShape(
  v: Partial<z.infer<typeof PaymentModeBaseSchema>>,
  ctx: z.RefinementCtx,
): void {
  if (v.credit_holder === 'third_party' && (v.allowed_entity_ids?.length ?? 0) === 0) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['allowed_entity_ids'], message: 'Con ente terzo indicare almeno un ente finanziante ammesso' });
  }
  if (v.credit_holder === 'brand' && (v.allowed_entity_ids?.length ?? 0) > 0) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['allowed_entity_ids'], message: 'Con l\'insegna come ente finanziante non si indicano enti terzi' });
  }
  if (v.credit_holder && v.ade_code == null && !v.mp_code) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['ade_code'], message: 'Indicare almeno un codice RT o fattura della quota finanziata' });
  }
}

export const RetailBrandPaymentModeCreateSchema = PaymentModeBaseSchema.superRefine(refinePaymentModeShape);
export type RetailBrandPaymentModeCreateInput = z.infer<typeof PaymentModeBaseSchema>;
export const RetailBrandPaymentModeUpdateSchema = PaymentModeBaseSchema.partial();
export type RetailBrandPaymentModeUpdateInput = z.infer<typeof RetailBrandPaymentModeUpdateSchema>;

/**
 * Campi di una modalità IN USO (referenziata da composizioni bundle) non
 * modificabili: cambiano l'invariante delle composizioni già agganciate
 * (ente, addebito effettivo, attese) o l'identità tecnica (code) usata da
 * snapshot e integrazioni → 409 IN_USE. Gli enti ammessi e gli addebiti
 * possono solo ALLARGARSI in uso (restringerli orfanerebbe composizioni).
 */
export const PAYMENT_MODE_LOCKED_IN_USE_FIELDS = ['brand_id', 'credit_holder', 'accounting_expectation', 'admin_expectation', 'code'] as const;
export type PaymentModeLockedInUseField = (typeof PAYMENT_MODE_LOCKED_IN_USE_FIELDS)[number];
/** Codici RT/fattura: modificabili anche in uso (default dell'insegna), ma con conferma esplicita dell'impatto. */
export const PAYMENT_MODE_SOFT_GUARD_FIELDS = ['ade_code', 'mp_code'] as const;

/** Impatto di una modalità sui listini (catalogo globale: conteggi su TUTTI i tenant). */
export const RetailBrandPaymentModeUsageSchema = z.object({
  mode_id: z.string().uuid(),
  compositions: z.number().int(),
  /** Composizioni attive (is_active) che usano la modalità. */
  active_compositions: z.number().int(),
  tenants: z.number().int(),
  price_lists: z.number().int(),
  /** Tenant che ereditano i codici RT/MP della modalità per default (senza abbinamento esplicito a un proprio metodo). */
  tenants_inheriting_codes: z.number().int(),
});
export type RetailBrandPaymentModeUsage = z.infer<typeof RetailBrandPaymentModeUsageSchema>;

export const PaymentModeWriteImpactSchema = z.object({
  kind: z.enum(['deactivated', 'codes_changed']),
  usage: RetailBrandPaymentModeUsageSchema,
});
export type PaymentModeWriteImpact = z.infer<typeof PaymentModeWriteImpactSchema>;

/** Risposta di POST/PATCH master: la modalità + effetti consentiti ma da comunicare. */
export const RetailBrandPaymentModeWriteResponseSchema = z.object({
  mode: RetailBrandPaymentModeSchema,
  impacts: z.array(PaymentModeWriteImpactSchema),
});
export type RetailBrandPaymentModeWriteResponse = z.infer<typeof RetailBrandPaymentModeWriteResponseSchema>;

/** Payload `details` del 409 IN_USE sulla PATCH master. */
export const RetailBrandPaymentModeInUseDetailsSchema = z.object({
  fields: z.array(z.string()),
  usage: RetailBrandPaymentModeUsageSchema,
});
export type RetailBrandPaymentModeInUseDetails = z.infer<typeof RetailBrandPaymentModeInUseDetailsSchema>;

/** Filtro lista master. */
export const RetailBrandPaymentModeListQuerySchema = z.object({
  brand_id: z.string().uuid().optional(),
  include_inactive: z.enum(['true', 'false']).optional(),
});
export type RetailBrandPaymentModeListQuery = z.infer<typeof RetailBrandPaymentModeListQuerySchema>;

/**
 * Modalità disponibili per un listino bundle (route tenant
 * `GET /pricelists/:id/payment-modes`): dell'insegna del listino, solo
 * attive. Include gli enti finanziatori del tenant con, per ciascuno, gli
 * id degli enti globali da cui derivano (match con `allowed_entities`).
 */
export const PriceListPaymentModeOptionSchema = RetailBrandPaymentModeSchema.pick({
  id: true,
  brand_id: true,
  brand_name: true,
  code: true,
  name: true,
  credit_holder: true,
  allowed_debits: true,
  allowed_entities: true,
  accounting_expectation: true,
  admin_expectation: true,
  ade_code: true,
  mp_code: true,
  available_products: true,
  sort_order: true,
});
export type PriceListPaymentModeOption = z.infer<typeof PriceListPaymentModeOptionSchema>;

export const PriceListFinancingPartnerOptionSchema = z.object({
  id: z.string().uuid(),
  legal_name: z.string(),
  trade_name: z.string().nullable(),
  is_active: z.boolean(),
  /** Id del preset globale da cui l'ente del tenant deriva (adozione, id diretto o P.IVA); null = ente locale non riconducibile. */
  global_entity_id: z.string().uuid().nullable(),
});
export type PriceListFinancingPartnerOption = z.infer<typeof PriceListFinancingPartnerOptionSchema>;

/**
 * Task #4334 — modalità referenziata da composizioni/tariffe del listino ma
 * NON più visibile all'organizzazione (insegna non gestita da alcuna ragione
 * sociale, prodotto non abilitato, disattivata): resta leggibile ma l'editor
 * la segnala e blocca il salvataggio finché non viene cambiata.
 */
export const PriceListUnavailablePaymentModeSchema = PriceListPaymentModeOptionSchema.extend({
  is_active: z.boolean(),
});
export type PriceListUnavailablePaymentMode = z.infer<typeof PriceListUnavailablePaymentModeSchema>;
export const PriceListPaymentModesResponseSchema = z.object({
  price_list_id: z.string().uuid(),
  retail_brand_id: z.string().uuid().nullable(),
  modes: z.array(PriceListPaymentModeOptionSchema),
  /** Modalità in uso sul listino ma non più visibili (badge + blocco save). */
  unavailable_modes: z.array(PriceListUnavailablePaymentModeSchema),
  financing_partners: z.array(PriceListFinancingPartnerOptionSchema),
});
export type PriceListPaymentModesResponse = z.infer<typeof PriceListPaymentModesResponseSchema>;

/** Codici errore tipizzati del dominio modalità (route listini). */
export const PAYMENT_MODE_ERROR_CODES = {
  NOT_ALLOWED_FOR_BRAND: 'POS_PAYMENT_MODE_NOT_ALLOWED_FOR_BRAND',
  INACTIVE: 'POS_PAYMENT_MODE_INACTIVE',
  PARTNER_REQUIRED: 'POS_PAYMENT_MODE_PARTNER_REQUIRED',
  PARTNER_FORBIDDEN: 'POS_PAYMENT_MODE_PARTNER_FORBIDDEN',
  PARTNER_NOT_FOUND: 'POS_PAYMENT_MODE_PARTNER_NOT_FOUND',
  /** Ente del tenant non riconducibile a un ente ammesso dal template. */
  PARTNER_NOT_ALLOWED: 'POS_PAYMENT_MODE_PARTNER_NOT_ALLOWED',
  /** Addebito effettivo non tra quelli ammessi dal template. */
  DEBIT_NOT_ALLOWED: 'POS_PAYMENT_MODE_DEBIT_NOT_ALLOWED',
  /** Modalità su una composizione fuori dai listini bundle. */
  MODE_BUNDLE_ONLY: 'POS_PAYMENT_MODE_BUNDLE_ONLY',
  /** Composizione bundle senza modalità. */
  MODE_REQUIRED: 'POS_PAYMENT_MODE_REQUIRED',
  FINANCED_REQUIRED: 'POS_PAYMENT_MODE_FINANCED_REQUIRED',
  /** Task #4333 — finanziato ≠ rate × rata oppure prezzo netto ≠ anticipo + finanziato. */
  FINANCED_MISMATCH: 'POS_PAYMENT_MODE_FINANCED_MISMATCH',
  /** Composizione non finanziata (device_promo) con campi finanziari valorizzati. */
  FINANCED_NOT_APPLICABLE: 'POS_PAYMENT_MODE_FINANCED_NOT_APPLICABLE',
  /** Composizione finanziata (bundle) con NDC fornitore attesa valorizzata (solo promozione prodotto). */
  SUPPLIER_CREDIT_NOTE_NOT_APPLICABLE: 'POS_COMPOSITION_SUPPLIER_CREDIT_NOTE_NOT_APPLICABLE',
  /** Task #4333 — ente finanziante del tenant esistente ma disattivato. */
  PARTNER_INACTIVE: 'POS_PAYMENT_MODE_PARTNER_INACTIVE',
  COMPOSITION_DUPLICATE: 'POS_COMPOSITION_DUPLICATE_UNIT',
  /** Task #4334 — modalità non visibile all'organizzazione (insegna/prodotto). */
  NOT_VISIBLE_FOR_TENANT: 'POS_PAYMENT_MODE_NOT_VISIBLE_FOR_TENANT',
  /** Task #4334 — abbinamento metodo↔modalità già assegnato a un altro metodo. */
  METHOD_BINDING_CONFLICT: 'POS_PAYMENT_MODE_METHOD_BINDING_CONFLICT',
} as const;
export type PaymentModeErrorCode = (typeof PAYMENT_MODE_ERROR_CODES)[keyof typeof PAYMENT_MODE_ERROR_CODES];

/**
 * Modalità visibili al tenant (predicato condiviso: di un'insegna gestita da una ragione sociale del tenant, prodotto del tenant abilitato,
 * attiva) con il metodo di incasso EFFETTIVO che la cassa userebbe per la
 * quota finanziata: scelto (abbinamento esplicito), ereditato per codici
 * default, oppure mancante/ambiguo (la vendita darebbe 422).
 */
export const FINANCED_QUOTA_METHOD_SOURCES = ['explicit', 'codes', 'missing', 'ambiguous'] as const;
export type FinancedQuotaMethodSource = (typeof FINANCED_QUOTA_METHOD_SOURCES)[number];
export const FINANCED_QUOTA_METHOD_SOURCE_LABELS: Record<FinancedQuotaMethodSource, string> = {
  explicit: 'Scelto',
  codes: 'Ereditato per codici',
  missing: 'Mancante',
  ambiguous: 'Ambiguo',
};

export const TenantPaymentModeEffectiveMethodSchema = z.object({
  mode: PriceListPaymentModeOptionSchema,
  source: z.enum(FINANCED_QUOTA_METHOD_SOURCES),
  payment_method_id: z.string().uuid().nullable(),
  payment_method_name: z.string().nullable(),
  /** Codici effettivi del metodo risolto (null se mancante/ambiguo). */
  effective_ade_code: PaymentModeAdeCodeSchema.nullable(),
  effective_mp_code: z.string().nullable(),
  /** Metodi candidati quando ambiguo (stessi codici, nessuno spareggio). */
  candidate_method_names: z.array(z.string()),
});
export type TenantPaymentModeEffectiveMethod = z.infer<typeof TenantPaymentModeEffectiveMethodSchema>;

export const TenantPaymentModesResponseSchema = z.object({
  product: TenantProductSchema,
  items: z.array(TenantPaymentModeEffectiveMethodSchema),
});
export type TenantPaymentModesResponse = z.infer<typeof TenantPaymentModesResponseSchema>;

/** Titolo del campo nel form/lista (master, tenant, gare). */
export const PAYMENT_MODE_FINANCED_FIELD_LABEL = 'Ente che finanzia';

/** Testo dei codici RT/fattura: default dell'insegna sovrascrivibile dal tenant. */
export const PAYMENT_MODE_CODES_FIELD_LABEL =
  'Codici RT/fattura della quota finanziata — default, il tenant può scegliere un proprio metodo';
